/** Account-backed MrGreenMode. Narrow optional game market data; explicitly
 * opened props widen it again. Account, order, fill and reference-odds streams
 * retain their existing delivery guarantees. */
import { useSyncExternalStore } from 'react';
import { useAuthStore } from './authStore';

const listeners = new Set<() => void>();
let fullFeedHolds = 0;
let lastPref = false;
let wired = false;

function prefOn(): boolean {
  return useAuthStore.getState().user?.prefs?.lowBandwidth === true;
}

function emit(): void {
  for (const fn of listeners) fn();
}

/** Follows the pref from first use, not at import: the route and the
 *  status bar import this module, and a test that mocks `authStore` without a
 *  `subscribe` must still load them (it simply reads "off"). */
function wire(): void {
  if (wired) return;
  wired = true;
  const store = useAuthStore as Partial<typeof useAuthStore> | undefined;
  if (typeof store?.getState !== 'function' || typeof store.subscribe !== 'function') return;
  lastPref = prefOn();
  store.subscribe(() => {
    const v = prefOn();
    if (v === lastPref) return;
    lastPref = v;
    emit();
  });
}

/** True when the signed-in trader has low bandwidth mode on. */
export function lowBandwidthEnabled(): boolean {
  wire();
  return lastPref;
}

/** True while some opened section holds the whole game ("show for this game"). */
export function fullFeedHeld(): boolean {
  return fullFeedHolds > 0;
}

/** Widens the tab back to the whole game while held. Returns the release
 *  (idempotent). Holds are counted; the scope changes on 0 ↔ 1 only. */
export function acquireFullFeed(): () => void {
  fullFeedHolds += 1;
  if (fullFeedHolds === 1) emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    fullFeedHolds = Math.max(0, fullFeedHolds - 1);
    if (fullFeedHolds === 0) emit();
  };
}

/** Fires when the mode or the full-feed hold changes the optional game scope. */
export function subscribeLowBandwidth(fn: () => void): () => void {
  wire();
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** React: is low bandwidth mode on for this trader? */
export function useLowBandwidth(): boolean {
  return useSyncExternalStore(subscribeLowBandwidth, lowBandwidthEnabled, () => false);
}

/** React: is some section holding the whole game right now? */
export function useFullFeedHeld(): boolean {
  return useSyncExternalStore(subscribeLowBandwidth, fullFeedHeld, () => false);
}

/** Tests only: drop holds and re-read the pref. */
export function __resetLowBandwidthForTests(): void {
  fullFeedHolds = 0;
  if (wired) lastPref = prefOn();
}
